import { useNavigate } from "react-router-dom";
import { Home, Shield, FileText, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_deal-dashboard-16/artifacts/mtal5hnl_0e8de284-294a-4a25-8f18-7d7cab9aad20.png";
const HERO_BG = "https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <img 
              src={LOGO_URL} 
              alt="Mitten Deals Realty" 
              className="h-14 w-auto"
              data-testid="nav-logo"
            />
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-sm font-semibold text-brand-primary hover:text-brand-orange transition-colors">
                About
              </a>
              <a href="#process" className="text-sm font-semibold text-brand-primary hover:text-brand-orange transition-colors">
                Our Process
              </a>
              <a href="#contact" className="text-sm font-semibold text-brand-primary hover:text-brand-orange transition-colors">
                Contact
              </a>
              <Button 
                onClick={() => navigate("/login")} 
                className="btn-primary"
                data-testid="nav-login-button"
              >
                Client Login
              </Button>
            </div>
            <Button 
              onClick={() => navigate("/login")} 
              className="md:hidden btn-primary"
              data-testid="mobile-login-button"
            >
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 hero-gradient" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6 animate-fadeIn" style={{ opacity: 0 }}>
            Exclusive Michigan
            <span className="block text-brand-orange">Wholesale Deals</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 animate-fadeIn stagger-1" style={{ opacity: 0 }}>
            Access off-market properties at wholesale prices. 
            Join our exclusive network of Michigan real estate investors.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn stagger-2" style={{ opacity: 0 }}>
            <Button 
              onClick={() => navigate("/login")}
              className="btn-primary text-lg px-8 py-4"
              data-testid="hero-login-button"
            >
              Client Portal
            </Button>
            <a 
              href="#contact"
              className="btn-outline bg-white/10 border-white text-white hover:bg-white/20 text-lg px-8 py-4 inline-flex items-center justify-center"
            >
              Request Access
            </a>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/70 rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-4">
              Why Choose Us
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-primary">
              Your Partner in Michigan Real Estate
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card-surface p-8 text-center">
              <div className="w-16 h-16 bg-brand-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Home className="w-8 h-8 text-brand-orange" />
              </div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">Off-Market Deals</h3>
              <p className="text-slate-600">
                Access exclusive properties before they hit the market. Our network brings you opportunities others never see.
              </p>
            </div>
            
            <div className="card-surface p-8 text-center">
              <div className="w-16 h-16 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-brand-green" />
              </div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">VIP Access</h3>
              <p className="text-slate-600">
                Members-only platform with personalized deal flow. Each investor sees opportunities tailored to their criteria.
              </p>
            </div>
            
            <div className="card-surface p-8 text-center">
              <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-brand-primary" />
              </div>
              <h3 className="text-xl font-semibold text-brand-primary mb-4">Seamless Closing</h3>
              <p className="text-slate-600">
                From PO to title work, manage everything in one place. Our platform streamlines the entire transaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange mb-4">
              How It Works
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-brand-primary">
              Simple, Secure, Streamlined
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Get Access", desc: "Request membership to our exclusive investor network" },
              { step: "02", title: "View Deals", desc: "Browse properties specifically selected for you" },
              { step: "03", title: "Submit PO", desc: "Secure your deal with a purchase order" },
              { step: "04", title: "Close", desc: "Complete title work and close seamlessly" }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="card-surface p-6">
                  <span className="text-5xl font-black text-slate-100">{item.step}</span>
                  <h3 className="text-lg font-semibold text-brand-primary mt-4 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-brand-orange" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={LOGO_URL} 
                alt="Mitten Deals Realty" 
                className="h-20 w-auto mb-8"
              />
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-6">
                Ready to Access Exclusive Deals?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Contact us to learn more about joining our network of successful Michigan real estate investors.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-orange/20 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Call Us</p>
                    <p className="font-semibold text-lg" data-testid="contact-phone">1 (888) 871-4928</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-orange/20 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Email Us</p>
                    <p className="font-semibold text-lg" data-testid="contact-email">admin@mittendeals.live</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-orange/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-brand-orange" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Location</p>
                    <p className="font-semibold text-lg">Michigan, USA</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card-surface p-8">
              <h3 className="text-xl font-semibold text-brand-primary mb-6">Request Access</h3>
              <p className="text-slate-600 mb-6">
                All accounts are issued by our administrator. Fill out this form and we'll be in touch.
              </p>
              <form className="space-y-4">
                <input 
                  type="text" 
                  placeholder="Full Name" 
                  className="input-field"
                  data-testid="contact-name-input"
                />
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  className="input-field"
                  data-testid="contact-email-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone Number" 
                  className="input-field"
                  data-testid="contact-phone-input"
                />
                <textarea 
                  placeholder="Tell us about your investment goals..." 
                  rows={4}
                  className="input-field resize-none"
                  data-testid="contact-message-input"
                />
                <Button 
                  type="button" 
                  className="btn-primary w-full"
                  data-testid="contact-submit-button"
                >
                  Submit Request
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#071018] border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src={LOGO_URL} 
              alt="Mitten Deals Realty" 
              className="h-10 w-auto"
            />
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Mitten Deals Realty. All rights reserved.
            </p>
            <Button 
              onClick={() => navigate("/login")} 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
              data-testid="footer-login-button"
            >
              Client Login
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
